import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/app/AppContext';
import { exportHouseholdBackup, MAX_BACKUP_BYTES, previewRestore, restoreBackup, type RestorePreview, type RestoreResult } from './backup';

export function BackupPanel({ onEditing }: { onEditing?: (editing: boolean) => void }) {
  const { household } = useApp();
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ text: string; preview: RestorePreview } | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<RestoreResult | null>(null);
  const working = useRef(false);
  const generation = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { onEditing?.(busy || draft !== null); }, [busy, draft, onEditing]);
  useEffect(() => () => { onEditing?.(false); }, [onEditing]);
  useEffect(() => {
    generation.current++;
    setDraft(null); setConfirmed(false); setError(''); setResult(null);
    if (input.current) input.current.value = '';
    return () => { generation.current++; };
  }, [household?.id, household?.cloud]);
  function start() {
    if (working.current) return false;
    working.current = true; setBusy(true); setError(''); setResult(null); return true;
  }
  function finish() { working.current = false; setBusy(false); }
  async function download() {
    if (!start()) return;
    try {
      const text = await exportHouseholdBackup();
      const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `kai-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not download backup.'); }
    finally { finish(); }
  }
  async function upload(file: File | undefined) {
    if (!file || !start()) return;
    const version = generation.current;
    setDraft(null); setConfirmed(false);
    try {
      if (file.size > MAX_BACKUP_BYTES) throw new Error('Backup exceeds the 25 MB limit.');
      const text = await file.text();
      const preview = await previewRestore(text);
      if (version === generation.current) setDraft({ text, preview });
    } catch (e) { if (version === generation.current) setError(e instanceof Error ? e.message : 'Could not read backup.'); }
    finally { finish(); if (input.current) input.current.value = ''; }
  }
  async function restore() {
    if (!draft || !confirmed || !start()) return;
    const version = generation.current;
    try {
      const restored = await restoreBackup(draft.text, draft.preview);
      if (version === generation.current) { setResult(restored); setDraft(null); setConfirmed(false); }
    } catch (e) {
      if (version === generation.current) { setError(e instanceof Error ? e.message : 'Could not restore backup.'); setDraft(null); setConfirmed(false); }
    } finally { finish(); }
  }
  return <section className="card p-5 space-y-4" aria-labelledby="backup-heading" aria-busy={busy}>
    <h2 id="backup-heading" className="text-lg font-semibold">Household backup</h2>
    <p>Download a private copy of this household. Backups are not encrypted, so keep them somewhere safe. An integrity check helps detect damaged or changed files.</p>
    <p>Restore adds profiles and records to the current local household while keeping existing data and preferences. Sign-in details are excluded. Cloud recovery is not available yet.</p>
    <button type="button" className="btn-ghost" disabled={busy || !household || draft !== null} onClick={() => void download()}>Download backup</button>
    <div><label className="label" htmlFor="backup-file">Choose JSON backup to preview</label><input ref={input} className="input py-2" id="backup-file" type="file" accept=".json,application/json" disabled={busy || !household || household.cloud} onChange={e => void upload(e.target.files?.[0])} /></div>
    {household?.cloud && <p>Restore is unavailable for this cloud household.</p>}
    {busy && <p role="status">Processing backup…</p>}
    {error && <p role="alert" className="text-danger">{error}</p>}
    {draft && <div className="space-y-3" aria-label="Restore preview">
      <h3 className="font-semibold">Restore preview</h3>
      <p>From {draft.preview.sourceHouseholdName} into {household?.name}.</p>
      {draft.preview.remapsHousehold && <p>This backup belongs to another household. Its profiles and records will be added here.</p>}
      <p>Add {draft.preview.babies} profiles, {draft.preview.events} events, {draft.preview.importBatches} import receipts and {draft.preview.predictionLog} predictions. Includes {draft.preview.tombstones} deleted records and {draft.preview.importMarkers} import deduplication markers. Skip {draft.preview.duplicates} identical records.</p>
      <p>{draft.preview.restoreFormulaWarning ? 'Recover the formula warning preference.' : 'Keep current preferences.'} {draft.preview.restoreActiveBaby ? 'Recover the active profile selection.' : 'Keep current profile selection.'}</p>
      <label className="flex gap-2"><input type="checkbox" checked={confirmed} disabled={busy} onChange={e => setConfirmed(e.target.checked)} />I reviewed this preview and confirm merging into {household?.name}.</label>
      <div className="flex flex-wrap gap-2"><button type="button" className="btn-primary" disabled={busy || !confirmed} onClick={() => void restore()}>Confirm restore</button><button type="button" className="btn-ghost" disabled={busy} onClick={() => { setDraft(null); setConfirmed(false); }}>Cancel restore</button></div>
    </div>}
    {result && <p role="status">Restore complete: added {result.babies} profiles, {result.events} events, {result.importBatches} import receipts and {result.predictionLog} predictions; skipped {result.duplicates} identical records.</p>}
  </section>;
}
